import type { ComponentType, ReactNode } from "react";
import { cn } from "~/lib/utils";
import { useConversationContext } from "~/pages/manifold-nextgen/components/conversations/ConversationContext";
import type { ArtifactPreview, ArtifactType } from "~/services/artifacts";
import { BlobArtifact } from "./BlobArtifact";
import { DataframeArtifact } from "./DataframeArtifact";
import { ExcelArtifact } from "./ExcelArtifact";
import { FigureArtifact } from "./FigureArtifact";
import {
  isGenomicsAlignmentArtifact,
  GenomicsAlignmentArtifact,
} from "./GenomicsAlignmentArtifact";
import { isHicContactMapArtifact, HicContactMapArtifact } from "./HicContactMapArtifact";
import { isHtmlArtifact, HtmlArtifact } from "./HtmlArtifact";
import { ImageArtifact } from "./ImageArtifact";
import { isProteinFeaturesArtifact, ProteinFeaturesArtifact } from "./ProteinFeaturesArtifact";
import { isProteinStructureArtifact, ProteinStructureArtifact } from "./ProteinStructureArtifact";
import { TableArtifact } from "./TableArtifact";
import { TextArtifact } from "./TextArtifact";
import { ValueArtifact } from "./ValueArtifact";
import { fileViewerFor } from "./file-viewers";
import { useArtifactPreview } from "./useArtifactPreview";

const RENDERERS: Record<ArtifactType, ComponentType<{ preview: ArtifactPreview }>> = {
  dataframe: DataframeArtifact,
  figure: FigureArtifact,
  value: ValueArtifact,
  text: TextArtifact,
  blob: BlobArtifact,
  image: ImageArtifact,
  excel: ExcelArtifact,
  table: TableArtifact,
};

// Resolves a single `[[name]]` reference and dispatches to the
// type-specific renderer. All artifact-renderer wiring funnels through
// here, so per-type components stay focused on display and never deal
// with fetching, loading, or unknown-type fallback.
export const ArtifactRef = ({ name }: { name: string }) => {
  const scope = useConversationContext();
  const { preview, error, isLoading, notFound, unsupported } = useArtifactPreview(scope, name);

  if (unsupported) {
    return (
      <Placeholder name={name}>
        <div className="py-2 text-xs text-muted-foreground">Artifact preview not available</div>
      </Placeholder>
    );
  }
  if (isLoading) {
    return (
      <Placeholder name={name}>
        <div className="h-16 animate-pulse rounded bg-muted/60" aria-label="Loading artifact" />
      </Placeholder>
    );
  }
  if (error) {
    return (
      <Placeholder name={name} tone="error">
        <div className="py-2 text-sm">Failed to load artifact</div>
      </Placeholder>
    );
  }
  if (notFound || !preview) {
    return (
      <Placeholder name={name}>
        <div className="py-2 text-sm text-muted-foreground">Artifact not found</div>
      </Placeholder>
    );
  }
  if (isProteinStructureArtifact(preview)) {
    return <ProteinStructureArtifact preview={preview} />;
  }
  if (isGenomicsAlignmentArtifact(preview)) {
    return <GenomicsAlignmentArtifact preview={preview} />;
  }
  if (isProteinFeaturesArtifact(preview)) {
    return <ProteinFeaturesArtifact preview={preview} />;
  }
  if (isHicContactMapArtifact(preview)) {
    return <HicContactMapArtifact preview={preview} />;
  }
  if (isHtmlArtifact(preview)) {
    return <HtmlArtifact preview={preview} />;
  }
  const fileViewer = fileViewerFor(preview);
  if (fileViewer) {
    return <fileViewer.Artifact preview={preview} />;
  }
  const Renderer = RENDERERS[preview.artifact_type];
  if (!Renderer) {
    return (
      <Placeholder name={preview.artifact_name ?? name}>
        <div className="py-2 text-sm text-muted-foreground">
          Unsupported artifact type: {preview.artifact_type}
        </div>
      </Placeholder>
    );
  }
  return <Renderer preview={preview} />;
};

// Minimal stand-in card for pre-resolution states. The full shell needs a
// preview (for toolbar/code wiring), which we don't yet have here — so
// loading, error, and not-found render this stripped-down version.
const Placeholder = ({
  name,
  tone,
  children,
}: {
  name: string;
  tone?: "error";
  children: ReactNode;
}) => (
  <div
    className={cn(
      "my-3 overflow-hidden rounded-lg border border-border bg-background/60",
      tone === "error" && "border-destructive/40 text-destructive"
    )}
  >
    <div className="border-b border-border bg-muted/40 px-3 py-1.5">
      <span className="truncate text-xs font-medium" title={name}>
        {name}
      </span>
    </div>
    <div className="px-3 py-2">{children}</div>
  </div>
);
